/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Local state and change handlers for appointment registration Certifications section.
 * @module hooks/useCertificationsSectionState
 */

import { useEffect, useRef, useState } from "react";
import { logger } from "../utils/logger.js";

export const useCertificationsSectionState = ({
  company,
  onFieldChange,
  onValidationChange,
}) => {
  const prevHasErrorsRef = useRef(false);
  const onValidationChangeRef = useRef(onValidationChange);

  const [selectedCertifications, setSelectedCertifications] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [documentedSystem, setDocumentedSystem] = useState(
    company?.tieneSistemaDocumentado ?? null,
  );
  const [esProveedorREPSE, setEsProveedorREPSE] = useState(
    company?.esProveedorREPSE || "",
  );
  const [tiemposPromedioEntrega, setTiemposPromedioEntrega] = useState(
    company?.tiemposPromedioEntrega || "",
  );
  const [metodosDePagoAceptados, setMetodosDePagoAceptados] = useState(
    company?.metodosDePagoAceptados || "",
  );
  const [seguroDeResponsabilidadCivil, setSeguroDeResponsabilidadCivil] =
    useState(company?.seguroDeResponsabilidadCivil || "");
  const [permisoIMMEX, setPermisoIMMEX] = useState(company?.permisoIMMEX || "");

  useEffect(() => {
    if (company?.certificaciones) {
      setSelectedCertifications(company.certificaciones);
    }
    if (company?.sectores) {
      setSectors(
        company.sectores.map((sector) => ({
          value: sector.value,
          experience: sector.experience || null,
        })),
      );
    }
  }, [company]);

  useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  }, [onValidationChange]);

  const handleInputChange = (field, value) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  const handleCheckboxChange = (value) => {
    logger.info("selectedCertifications", { data: selectedCertifications });

    const newSelectedCertifications = selectedCertifications.includes(value)
      ? selectedCertifications.filter((certification) => certification !== value)
      : [...selectedCertifications, value];

    setSelectedCertifications(newSelectedCertifications);
    handleInputChange("certificaciones", newSelectedCertifications);
  };

  const handleSectorCheckboxChange = (sectorValue) => {
    const sectorExists = sectors.some((sector) => sector.value === sectorValue);

    if (sectorExists) {
      const newSectors = sectors.filter((sector) => sector.value !== sectorValue);
      setSectors(newSectors);
      handleInputChange("sectores", newSectors);
      return;
    }

    const newSectors = [...sectors, { value: sectorValue, experience: null }];
    setSectors(newSectors);
    handleInputChange("sectores", newSectors);
  };

  const handleSectorExperienceChange = (sectorValue, selectedOption) => {
    const newSectors = sectors.map((sector) =>
      sector.value === sectorValue
        ? { ...sector, experience: selectedOption.value }
        : sector,
    );
    setSectors(newSectors);
    handleInputChange("sectores", newSectors);
  };

  const handleDocumentedSystemChange = (value) => {
    setDocumentedSystem(value);
    handleInputChange("tieneSistemaDocumentado", value);
  };

  const handleSelectFieldChange = (setter, fieldKey) => (selectedOption) => {
    setter(selectedOption.value);
    handleInputChange(fieldKey, selectedOption.value);
  };

  return {
    selectedCertifications,
    sectors,
    documentedSystem,
    esProveedorREPSE,
    tiemposPromedioEntrega,
    metodosDePagoAceptados,
    seguroDeResponsabilidadCivil,
    permisoIMMEX,
    setEsProveedorREPSE,
    setTiemposPromedioEntrega,
    setMetodosDePagoAceptados,
    setSeguroDeResponsabilidadCivil,
    setPermisoIMMEX,
    prevHasErrorsRef,
    onValidationChangeRef,
    handleCheckboxChange,
    handleSectorCheckboxChange,
    handleSectorExperienceChange,
    handleDocumentedSystemChange,
    handleSelectFieldChange,
    handleInputChange,
  };
};
